<script lang="ts">
  import { t } from './i18n.svelte'
  import { friends } from './friends.svelte'
  import { handleProblem, normalizeHandle, HANDLE_MAX } from './handle'

  /**
   * A text box for an @handle that says, as you type, whether it can be taken:
   * the shape is checked here at once, availability is asked of the server a
   * moment after you stop typing. `ok` is true only for a handle that is free
   * (or already yours). With `autofix`, a taken starting value is swapped once
   * for the server's nearest free suggestion.
   */
  let {
    value = $bindable(''),
    // eslint-disable-next-line no-useless-assignment -- the default is the initial bindable value
    ok = $bindable(false),
    current = null,
    autofix = false,
    id = 'handle',
  }: {
    value?: string
    ok?: boolean
    /** The account's present handle, which counts as available. */
    current?: string | null
    autofix?: boolean
    id?: string
  } = $props()

  type Phase = 'idle' | 'checking' | 'ok' | 'bad'
  let phase = $state<Phase>('idle')
  let message = $state('')
  let suggestion = $state<string | null>(null)
  let sequence = 0
  let fixed = false

  $effect(() => {
    const handle = normalizeHandle(value)
    const mine = current
    const seq = ++sequence
    suggestion = null
    ok = false
    if (!handle) {
      phase = 'idle'
      message = ''
      return
    }
    if (handle === mine) {
      phase = 'ok'
      message = t('This is your handle')
      ok = true
      return
    }
    const problem = handleProblem(handle)
    if (problem) {
      phase = 'bad'
      message = t(problem)
      return
    }
    phase = 'checking'
    message = t('Checking…')
    const timer = setTimeout(async () => {
      const check = await friends.checkHandle(handle)
      if (seq !== sequence) return
      if (check.state === 'available' || check.state === 'yours') {
        phase = 'ok'
        message = t('@{handle} is available', { handle })
        ok = true
      } else if (check.state === 'taken') {
        if (autofix && !fixed && check.suggestion) {
          fixed = true
          value = check.suggestion
          return
        }
        phase = 'bad'
        message = t('@{handle} is taken', { handle })
        suggestion = check.suggestion
      } else {
        phase = 'bad'
        message = check.reason ? t(check.reason) : t('That handle is not allowed.')
      }
    }, 300)
    return () => clearTimeout(timer)
  })
</script>

<div class="field">
  <label for={id}>{t('Handle')}</label>
  <div class="handle-input" class:handle-input--bad={phase === 'bad'}>
    <span aria-hidden="true">@</span>
    <input
      {id}
      bind:value
      maxlength={HANDLE_MAX + 1}
      autocomplete="off"
      autocapitalize="none"
      spellcheck="false"
      aria-describedby="{id}-status"
      aria-invalid={phase === 'bad'}
    />
  </div>
  <div id="{id}-status" class="handle-status handle-status--{phase}" aria-live="polite">
    {message}
    {#if suggestion}
      <button type="button" class="link-btn" onclick={() => (value = suggestion ?? value)}
        >{t('Use @{handle}', { handle: suggestion })}</button
      >
    {/if}
  </div>
</div>

<style>
  .handle-input {
    display: flex;
    align-items: center;
    gap: 2px;
    padding: 0 12px;
    background: var(--bg-input);
    border: 1px solid var(--border-strong);
    border-radius: var(--radius);
  }
  .handle-input:focus-within {
    border-color: var(--accent);
  }
  .handle-input--bad {
    border-color: var(--live);
  }
  .handle-input span {
    color: var(--text-faint);
    font-weight: 600;
  }
  .handle-input input {
    flex: 1;
    min-width: 0;
    border: 0;
    outline: 0;
    background: transparent;
    padding: 10px 0;
  }
  .handle-status {
    min-height: 1.25rem;
    margin-top: 6px;
    font-size: 0.8125rem;
    color: var(--text-faint);
  }
  .handle-status--ok {
    color: var(--ok);
  }
  .handle-status--bad {
    color: var(--live);
  }
</style>
